<template>
  <div class="tw-premium-shell tw-mobile-glass-shell">
    <div
      class="mobile-glass-scene lg:tw-hidden"
      aria-hidden="true"
    >
      <div class="mobile-glass-scene__mesh" />
      <div class="mobile-glass-scene__orb mobile-glass-scene__orb--lavender" />
      <div class="mobile-glass-scene__orb mobile-glass-scene__orb--aqua" />
      <div class="mobile-glass-scene__frost" />
      <div class="mobile-glass-scene__shimmer" />
    </div>

    <div class="tw-premium-shell__content">
    <div
      class="mobile-search-stack"
      :class="{
        'mobile-search-stack--active': hasUserSearched,
        'mobile-search-stack--pinned': searchHeaderPinned,
      }"
    >
      <search-hero
        :search-string="searchString"
        :suggestions="suggestions"
        :show-explanations="showExplanations"
        :show-category-chips="showCategoryChips"
        :compact="hasUserSearched"
        :in-mobile-search-stack="hasUserSearched"
        @input-change="onInputChange"
        @selected="onSelected"
        @search="onEnter"
        @click="clickHandler"
        @go-home="goHome"
        @category-selected="SearchForCat"
        @pinned-change="searchHeaderPinned = $event"
      />

      <div
        v-if="showMobileHistogram"
        class="mobile-search-stack__histogram tw-premium-container lg:tw-hidden"
      >
        <price-scroll-indicator
          :products="displayedComparedItems"
          :price-bounds="histogramPriceBounds"
          :currency-sign="currencyAndSign[selectedCurrency] || '$'"
          :hide-accessories="activeFilters.hideAccessories === true"
          :search-query="searchString"
          :sticky="false"
          @bar-click="scrollToPriceBracket"
        />
      </div>
    </div>

    <results-toolbar
      class="results-toolbar"
      :class="{ 'results-toolbar--mobile-search': hasUserSearched }"
      :country-flag-url="countryFlagUrl"
      :selected-country="selectedCountry"
      :selected-currency="selectedCurrency"
      :currency-sign="currencyAndSign[selectedCurrency] || '$'"
      :result-count="displayedComparedItems.length"
      :selected-sorting="selectedSorting"
      @sort="runSort"
      @shipping-change="onShippingChange"
      @toggle-locale="setLocale(locale === 'he' ? 'en' : 'he')"
    />

    <main
      class="tw-premium-container tw-mobile-shell-pad"
      :class="hasUserSearched ? 'tw-pt-4' : 'tw-py-6'"
    >
      <home-discovery
        v-if="isHomeDiscovery"
        :country="selectedCountry"
        :currency="selectedCurrency"
        :currency-sign="currencyAndSign[selectedCurrency] || '$'"
        @popular-search="SearchForCat"
      />

      <div v-else class="tw-grid tw-gap-6 lg:tw-grid-cols-[280px_1fr]">
        <!-- Desktop sidebar -->
        <div class="tw-hidden lg:tw-block">
          <div
            class="filter-sidebar-sticky tw-sticky"
            :style="filterSidebarStickyStyle"
          >
            <filter-sidebar
              v-model="activeFilters"
              :price-bounds="priceBounds"
              :products="sourceComparedItems"
              :currency-sign="currencyAndSign[selectedCurrency] || '$'"
              :search-query="searchString"
              @scroll-to-price-bracket="scrollToPriceBracket"
            />
          </div>
        </div>

        <section class="tw-min-w-0">
          <div
            v-if="comparePage"
            class="compare-seo-block tw-mb-6 tw-rounded-card tw-border tw-border-slate-200 tw-bg-white tw-p-5 tw-shadow-sm"
          >
            <h1 class="tw-text-2xl tw-font-bold tw-text-slate-900 sm:tw-text-3xl">
              {{ compareSeoTitle }}
            </h1>
            <p class="tw-mt-3 tw-text-base tw-leading-relaxed tw-text-slate-600">
              {{ compareSeoDescription }}
            </p>
            <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
              {{ compareSeoSubtext }}
            </p>
          </div>

          <product-grid
            :products="displayedComparedItems"
            :loading="showLoading"
            :card-props="cardProps"
            :has-active-filters="hasActiveFilters"
            :suppress-transitions="suppressListTransitions"
          />

          <div v-if="isLoadingMore" class="tw-py-4 tw-text-center tw-text-sm tw-text-muted">
            Loading more results...
          </div>

          <div v-if="!isLoadMore && !showErrorMessage && !showLoading && !isLoadingMore && displayedComparedItems.length" class="tw-py-8 tw-text-center tw-text-sm tw-text-muted">
            Sorry, no more results...
          </div>

          <div v-if="showErrorMessage" class="tw-rounded-card tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-6 tw-text-center tw-text-rose-700">
            {{ errorMsg }}
          </div>
        </section>
      </div>
    </main>

    <!-- Mobile off-canvas filter drawer -->
    <filter-drawer
      :open="filterDrawerOpen"
      v-model="activeFilters"
      :price-bounds="histogramPriceBounds"
      :products="displayedComparedItems"
      :currency-sign="currencyAndSign[selectedCurrency] || '$'"
      :search-query="searchString"
      @close="filterDrawerOpen = false"
      @scroll-to-price-bracket="scrollToPriceBracket"
    />

    <!-- Mobile sticky bottom bar -->
    <mobile-bottom-bar
      v-if="hasUserSearched"
      class="lg:tw-hidden"
      :selected-sorting="selectedSorting"
      :has-active-filters="hasActiveFilters"
      @sort="runSort"
      @open-filters="openFilters"
    />

    <b-modal hide-header-close no-close-on-esc no-close-on-backdrop v-model="showModal" id="modalPopover" centered hide-footer>
      <div class="tw-text-center">
        <img src="img/cookie.png" class="tw-mx-auto tw-h-24" alt="cookie">
        <p class="tw-mt-4 tw-text-sm tw-leading-6 tw-text-slate-600">
          We use cookies to improve your browsing experience, show personalized content, analyze site traffic, and understand where our audience is coming from.<br>
          By choosing I Accept, you consent to our
          <a href="https://www.termsfeed.com/live/b42b2cf2-4847-4b76-8130-f24edb695618" target="_blank">Cookie Policy</a>
        </p>
        <b-button class="accept-btn" block @click="cookiesAccepted">Accept</b-button>
      </div>
    </b-modal>

    <button
      @click="scrollToTop"
      v-show="visible"
      type="button"
      id="scroll-to-top-button"
      class="tw-pressable tw-fixed tw-right-6 tw-z-40 tw-rounded-full tw-bg-brand tw-p-3 tw-text-white tw-shadow-card hover:tw-bg-brand-dark tw-bottom-24 lg:tw-bottom-6"
    >
      ↑
    </button>

    <main-footer id="footer" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MainFooter from '.././layout/MainFooter.vue';
import SearchHero from './components/SearchHero.vue';
import ResultsToolbar from './components/ResultsToolbar.vue';
import FilterSidebar from './components/FilterSidebar.vue';
import FilterDrawer from './components/FilterDrawer.vue';
import MobileBottomBar from './components/MobileBottomBar.vue';
import PriceScrollIndicator from './components/PriceScrollIndicator.vue';
import ProductGrid from './components/ProductGrid.vue';
import HomeDiscovery from './components/HomeDiscovery.vue';
import { filterProducts, DEFAULT_FILTERS, getPriceBounds, hasActiveFilters } from '@/utils/productFilters';
import { findFirstProductInBracket } from '@/utils/priceHistogram';
import { getProductKey, getLowestStorePrice, findBestDealProductId } from '@/utils/priceUtils';
import { getComparePageBySlug, SITE_URL } from '@/seo/comparePages';
import {
  setPageMeta,
  injectJsonLd,
  removeJsonLd,
  buildComparePageSchema,
  buildDefaultHomeMeta,
} from '@/seo/seoUtils';
import { grantAnalyticsConsent } from '@/utils/analytics';
import {
  getSavedCountry,
  getSavedCurrency,
  getSavedSort,
  initUserPreferences,
  saveShippingPreferences,
  saveSort,
} from '@/utils/userPreferences';

export default {
  name: 'landing',
  bodyClass: 'landing-page',
  components: {
    MainFooter,
    SearchHero,
    ResultsToolbar,
    FilterSidebar,
    FilterDrawer,
    MobileBottomBar,
    PriceScrollIndicator,
    ProductGrid,
    HomeDiscovery,
  },
  data() {
    return {
      searchString: '',
      rawResult: { comparedItems: [] },
      ebayItemsRawResult: '',
      amazonItemsRawResult: '',
      selectedCurrency: getSavedCurrency(),
      selectedCountry: getSavedCountry(),
      acceptCookies: '',
      currencyAndSign: { NIS: '₪', ILS: '₪', USD: '$', EUR: '€', GBP: '£', RUB: '₽' },
      showLoading: false,
      isLoadingMore: false,
      offset: 0,
      stopLoadingMore: false,
      lockScrolling: false,
      scrollNavActive: false,
      lastScrollTop: 0,
      showModal: false,
      visible: false,
      errorMsg: '',
      showErrorMessage: false,
      isLoadMore: true,
      awaitingSearch: false,
      suggestions: [],
      countryFlagUrl: `/img/flags/${getSavedCountry()}.png`,
      selectedSorting: getSavedSort(),
      compopoSortedItems: { comparedItems: [] },
      isNewUser: false,
      hasUserSearched: false,
      activeFilters: { ...DEFAULT_FILTERS },
      filterDrawerOpen: false,
      suppressListTransitions: false,
      scrollNavToken: 0,
      scrollNavTimer: null,
      searchHeaderPinned: false,
    };
  },
  computed: {
    sourceComparedItems() {
      if (this.rawResult && this.rawResult.comparedItems) {
        return this.rawResult.comparedItems;
      }
      return [];
    },
    displayedComparedItems() {
      return filterProducts(this.sourceComparedItems, this.activeFilters, this.searchString);
    },
    priceBounds() {
      return getPriceBounds(this.sourceComparedItems);
    },
    histogramPriceBounds() {
      return getPriceBounds(this.displayedComparedItems);
    },
    bestDealProductId() {
      return findBestDealProductId(this.displayedComparedItems);
    },
    showExplanations() {
      return !this.hasUserSearched && !this.showLoading;
    },
    showCategoryChips() {
      return !this.hasUserSearched;
    },
    showMobileHistogram() {
      return this.hasUserSearched && !this.showLoading && this.displayedComparedItems.length > 0;
    },
    isHomeDiscovery() {
      return !this.hasUserSearched && !this.comparePage;
    },
    hasActiveFilters() {
      return hasActiveFilters(this.activeFilters);
    },
    comparePage() {
      const slug = this.$route.params.slug;
      return slug ? getComparePageBySlug(slug) : null;
    },
    compareSeoTitle() {
      if (!this.comparePage) return '';
      return this.locale === 'he' ? this.comparePage.titleHe : this.comparePage.titleEn;
    },
    compareSeoDescription() {
      if (!this.comparePage) return '';
      return this.locale === 'he' ? this.comparePage.descriptionHe : this.comparePage.descriptionEn;
    },
    compareSeoSubtext() {
      return this.locale === 'he'
        ? 'השוואת מחירים מ-eBay, AliExpress ו-Amazon כולל משלוח'
        : 'Price comparison from eBay, AliExpress and Amazon including shipping';
    },
    filterSidebarStickyStyle() {
      if (this.hasUserSearched) {
        return {
          top: '5.5rem',
          '--filter-sidebar-max-height': 'calc(100vh - 6.5rem)',
        };
      }

      return {
        top: '1rem',
        '--filter-sidebar-max-height': 'calc(100vh - 2rem)',
      };
    },
  },
  methods: {
    cardProps(product) {
      const key = getProductKey(product);
      return {
        itemTitle: product.otherStores[0].title,
        itemImageUrl: product.otherStores[0].image.imageUrl,
        currencySign: this.currencyAndSign[this.selectedCurrency] || '$',
        items: product.otherStores,
        itemType: product.itemType,
        isBestDeal: this.bestDealProductId === key,
        shipToCountry: this.selectedCountry,
        productKey: key,
        searchQuery: this.searchString,
      };
    },
    runSort(method) {
      this.selectedSorting = method;
      saveSort(method);
      if (!this.rawResult || !this.rawResult.comparedItems) {
        return false;
      }
      if (method !== 'compopo-sort') {
        const sorted = this.sortedItemsByPrice(method);
        const current = this.rawResult.comparedItems;
        const orderChanged = sorted.length !== current.length
          || sorted.some((item, index) => getProductKey(item) !== getProductKey(current[index]));
        if (orderChanged) {
          this.rawResult.comparedItems = sorted;
        }
        return orderChanged;
      }
      this.rawResult = JSON.parse(JSON.stringify(this.compopoSortedItems));
      return true;
    },
    sortedItemsByPrice(sortType) {
      const direction = sortType === 'lowest-first' ? -1 : 1;
      return [...this.rawResult.comparedItems].sort((a, b) => {
        const aPrice = getLowestStorePrice(a);
        const bPrice = getLowestStorePrice(b);
        if (aPrice < bPrice) return direction;
        if (aPrice > bPrice) return direction * -1;
        return 0;
      });
    },
    SearchForCat(category) {
      this.searchString = category;
      this.clicked(true, false);
    },
    updatePageSeo() {
      if (this.comparePage) {
        const title = this.compareSeoTitle;
        const description = this.compareSeoDescription;
        setPageMeta({
          title: `${title} | Compopo`,
          description,
          canonical: `${SITE_URL}/compare/${this.comparePage.slug}`,
          locale: this.locale,
        });
        this.updateCompareSchema();
        return;
      }
      const meta = buildDefaultHomeMeta(this.locale);
      setPageMeta({
        title: meta.title,
        description: meta.description,
        canonical: meta.canonical,
        locale: meta.locale,
      });
      removeJsonLd();
    },
    updateCompareSchema() {
      if (!this.comparePage) {
        removeJsonLd();
        return;
      }
      injectJsonLd(buildComparePageSchema(
        this.comparePage,
        this.locale,
        this.displayedComparedItems,
      ));
    },
    applyRouteSearch() {
      if (this.$route.params.slug) {
        if (!this.comparePage) {
          this.$router.replace('/compare');
          return;
        }
        this.searchString = this.comparePage.searchQuery;
        this.updatePageSeo();
        this.clicked(true, false);
        return;
      }

      this.searchString = this.$route.query.search || '';
      this.updatePageSeo();
      if (this.searchString) {
        this.clicked(true, false);
        return;
      }

      this.hasUserSearched = false;
      this.rawResult = { comparedItems: [] };
      this.showLoading = false;
      this.isLoadingMore = false;
    },
    cookiesAccepted() {
      this.showModal = false;
      this.$cookie.set('acceptCookies', 'true', 365);
      this.isNewUser = false;
      grantAnalyticsConsent();
      this.sendCookiesAccpeted();
    },
    openFilters() {
      this.filterDrawerOpen = true;
    },
    scrollToPriceBracket({ min, max, firstProductKey }) {
      this.filterDrawerOpen = false;

      if (this.scrollNavTimer) {
        clearTimeout(this.scrollNavTimer);
        this.scrollNavTimer = null;
      }
      const navToken = this.scrollNavToken + 1;
      this.scrollNavToken = navToken;
      this.scrollNavActive = true;

      const orderChanged = this.runSort('lowest-first');
      this.suppressListTransitions = orderChanged;
      this.lockScrolling = true;

      const product = findFirstProductInBracket(this.displayedComparedItems, min, max);
      const key = firstProductKey || (product ? getProductKey(product) : null);
      if (!key) {
        this.suppressListTransitions = false;
        this.lockScrolling = false;
        this.scrollNavActive = false;
        return;
      }

      const settleMs = orderChanged ? 400 : 0;
      this.scrollNavTimer = setTimeout(() => {
        if (this.scrollNavToken !== navToken) return;

        this.$nextTick(() => {
          this.scrollToProductKey(key, navToken, () => {
            this.suppressListTransitions = false;
            this.lockScrolling = false;
            setTimeout(() => {
              if (this.scrollNavToken === navToken) {
                this.scrollNavActive = false;
              }
            }, 800);
          });
        });
      }, settleMs);
    },
    scrollToProductKey(key, navToken, onDone) {
      const offset = 110;
      let frame = 0;
      const maxFrames = 12;

      const tryScroll = () => {
        if (this.scrollNavToken !== navToken) return;

        const card = document.querySelector(`[data-product-key="${CSS.escape(key)}"]`);
        if (!card) {
          if (frame < maxFrames) {
            frame += 1;
            requestAnimationFrame(tryScroll);
            return;
          }
          if (onDone) onDone();
          return;
        }

        const top = card.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: frame === 0 ? 'smooth' : 'auto',
        });

        frame += 1;
        if (frame <= 5) {
          requestAnimationFrame(tryScroll);
        } else if (onDone) {
          onDone();
        }
      };

      requestAnimationFrame(tryScroll);
    },
    scrollToTop() {
      this.intervalId = setInterval(() => {
        if (window.pageYOffset === 0) {
          clearInterval(this.intervalId);
        }
        window.scroll(0, window.pageYOffset - 50);
      }, 1);
    },
    scrollListener() {
      this.visible = window.scrollY > 1;
    },
    getMoreResults() {
      window.onscroll = () => {
        const scrollTop = document.documentElement.scrollTop || window.pageYOffset;
        const scrollingDown = scrollTop > this.lastScrollTop + 8;
        this.lastScrollTop = scrollTop;

        if (!scrollingDown || this.scrollNavActive || this.lockScrolling || this.isLoadingMore) {
          return;
        }

        const bottomOfWindow = scrollTop + window.innerHeight >= document.documentElement.offsetHeight * 0.92;
        if (bottomOfWindow && !this.stopLoadingMore && this.hasUserSearched && this.searchString) {
          this.stopLoadingMore = true;
          this.offset += 1;
          this.clicked(false, false);
        }
      };
    },
    sendCookiesAccpeted() {
      axios.get('/api/v1/cookieAccepted')
        .then((res) => {
          this.$cookie.set('selectedCurrency', res.data.currency, 365);
          this.$cookie.set('selectedCountry', res.data.country_code, 365);
          this.selectedCountry = res.data.country_code;
          this.selectedCurrency = res.data.currency === 'ILS' ? 'NIS' : res.data.currency;
          saveShippingPreferences(this.selectedCountry, this.selectedCurrency);
          this.countryFlagUrl = `/img/flags/${res.data.country_code}.png`;
        })
        .catch(() => {
          this.errorMsg = 'Somthing went wrong while accepting cookie...';
          this.showErrorMessage = true;
        });
    },
    clicked(firstSearch = true, initial = false) {
      if (!initial) {
        this.hasUserSearched = true;
      }
      this.showErrorMessage = false;
      if (firstSearch) {
        this.rawResult = { comparedItems: [] };
      }
      if (!initial) {
        this.lockScrolling = false;
      }
      if (firstSearch) {
        this.showLoading = true;
      } else {
        this.isLoadingMore = true;
      }
      this.selectedCountry = this.$cookie.get('selectedCountry') || getSavedCountry();
      this.selectedCurrency = this.$cookie.get('selectedCurrency') || getSavedCurrency();
      if (this.selectedCurrency === 'ILS') {
        this.selectedCurrency = 'NIS';
      }
      saveShippingPreferences(this.selectedCountry, this.selectedCurrency);
      this.countryFlagUrl = `/img/flags/${this.selectedCountry}.png`;

      const data = {
        searchString: this.searchString,
        country: this.selectedCountry,
        currency: this.selectedCurrency,
        conditions: this.activeFilters.conditions,
        offset: firstSearch ? 0 : this.offset,
      };

      const ebayRequest = axios({ method: 'post', url: '/api/v1/browse/', data });
      const amazonRequest = axios({ method: 'post', url: '/api/v1/browseAmazon/', data });

      axios.all([ebayRequest, amazonRequest]).then(axios.spread((ebayData, amazonData) => {
        const ebayItems = (ebayData.data && ebayData.data.comparedItems) ? ebayData.data.comparedItems : [];
        const amazonItems = (amazonData.data && amazonData.data.comparedItems) ? amazonData.data.comparedItems : [];
        const nextItems = ebayItems.concat(amazonItems);

        if (!firstSearch) {
          this.rawResult.comparedItems = this.rawResult.comparedItems.concat(nextItems);
        } else {
          this.offset = 0;
          this.rawResult = {
            ...(ebayData.data || {}),
            comparedItems: nextItems,
          };
          this.compopoSortedItems = JSON.parse(JSON.stringify(this.rawResult));
        }

        this.isLoadMore = !!(ebayData.data && ebayData.data.isLoadMore);
        this.showLoading = false;
        this.isLoadingMore = false;
        this.stopLoadingMore = !this.isLoadMore;
        if (firstSearch) {
          if (this.selectedSorting !== 'compopo-sort') {
            this.runSort(this.selectedSorting);
          }
          this.updateCompareSchema();
        }
      })).catch((error) => {
        if (error.response && error.response.data === 'Hebrew chars are not supported') {
          this.errorMsg = 'קשה לנו עם עברית... אנא חפש באנגלית';
        } else {
          this.errorMsg = 'Sorry, but something went wrong... Please try again in a few seconds.';
        }
        this.showErrorMessage = true;
        this.showLoading = false;
        this.isLoadingMore = false;
      });
    },
    clickHandler() {},
    onSelected(item) {
      if (item) {
        this.searchString = item.item;
        this.suggestions = [{ data: [] }];
        this.clicked(true, false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    onInputChange(text) {
      this.searchString = text;
      if (!this.awaitingSearch) {
        setTimeout(() => {
          if (text !== '') {
            this.sendQueryForCompletion(text);
          }
          this.awaitingSearch = false;
        }, 100);
      }
      this.awaitingSearch = true;
    },
    sendQueryForCompletion(text) {
      axios({
        method: 'post',
        url: '/api/v1/getAutoComplete/',
        data: { searchString: text },
      })
        .then((res) => {
          this.suggestions = [{ data: res.data || [] }];
        })
        .catch(() => {});
    },
    onEnter() {
      if (this.searchString !== '') {
        this.clicked(true, false);
        this.suggestions = [{ data: [] }];
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    goHome() {
      this.searchString = '';
      this.hasUserSearched = false;
      this.showErrorMessage = false;
      this.activeFilters = { ...DEFAULT_FILTERS };
      this.offset = 0;
      window.scrollTo(0, 0);

      if (this.$route.path !== '/') {
        this.$router.push('/');
      } else {
        this.rawResult = { comparedItems: [] };
        this.showLoading = false;
        this.isLoadingMore = false;
        this.updatePageSeo();
      }
    },
    onShippingChange({ country, currency }) {
      const prevCountry = this.selectedCountry;
      const prevCurrency = this.selectedCurrency;
      const normalizedCurrency = currency === 'ILS' ? 'NIS' : currency;

      this.selectedCountry = country;
      this.selectedCurrency = normalizedCurrency;
      this.$cookie.set('selectedCountry', country, 365);
      this.$cookie.set('selectedCurrency', normalizedCurrency, 365);
      saveShippingPreferences(country, normalizedCurrency);
      this.countryFlagUrl = `/img/flags/${country}.png`;

      if (prevCountry !== country || prevCurrency !== normalizedCurrency) {
        if (this.hasUserSearched && this.searchString) {
          this.clicked(true, false);
        }
      }
    },
  },
  watch: {
    'activeFilters.conditions': {
      deep: true,
      handler() {
        if (this.hasUserSearched && this.searchString) {
          this.clicked(true, false);
        }
      },
    },
    '$route'() {
      this.applyRouteSearch();
    },
    locale() {
      this.updatePageSeo();
    },
    displayedComparedItems() {
      if (this.comparePage && !this.showLoading) {
        this.updateCompareSchema();
      }
    },
  },
  mounted() {
    this.acceptCookies = this.$cookie.get('acceptCookies');
    if (this.acceptCookies == null) {
      this.isNewUser = true;
      this.showModal = true;
    }

    initUserPreferences((key) => this.$cookie.get(key));

    this.selectedCurrency = getSavedCurrency();
    this.selectedCountry = getSavedCountry();
    this.selectedSorting = getSavedSort();
    this.countryFlagUrl = `/img/flags/${this.selectedCountry}.png`;

    this.applyRouteSearch();

    const seoPrerender = document.getElementById('seo-prerender');
    if (seoPrerender) {
      seoPrerender.remove();
    }

    this.lockScrolling = false;
    this.lastScrollTop = window.pageYOffset || 0;
    this.getMoreResults();
    window.addEventListener('scroll', this.scrollListener);
  },
  destroyed() {
    window.removeEventListener('scroll', this.scrollListener);
    if (this.scrollNavTimer) {
      clearTimeout(this.scrollNavTimer);
    }
  },
};
</script>

<style scoped>
.accept-btn {
  border-radius: 999px !important;
  background: #2563eb !important;
  border-color: #2563eb !important;
}

.mobile-glass-scene {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.mobile-glass-scene__mesh {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(ellipse 120% 80% at 12% 8%, rgba(196, 181, 253, 0.72) 0%, transparent 62%),
    radial-gradient(ellipse 110% 75% at 90% 88%, rgba(153, 246, 228, 0.62) 0%, transparent 58%),
    radial-gradient(ellipse 90% 60% at 50% 45%, rgba(186, 170, 255, 0.28) 0%, transparent 65%),
    linear-gradient(175deg, #f0ecff 0%, #e8e2ff 32%, #dff7f4 68%, #edf8ff 100%);
}

.mobile-glass-scene__orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(72px);
}

.mobile-glass-scene__orb--lavender {
  top: -10%;
  left: -20%;
  width: 85%;
  height: 50%;
  background: radial-gradient(circle, rgba(186, 164, 255, 0.75) 0%, transparent 72%);
  opacity: 0.68;
}

.mobile-glass-scene__orb--aqua {
  bottom: -10%;
  right: -18%;
  width: 80%;
  height: 52%;
  background: radial-gradient(circle, rgba(126, 230, 210, 0.68) 0%, transparent 72%);
  opacity: 0.62;
}

.mobile-glass-scene__frost {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.26);
  backdrop-filter: blur(40px) saturate(155%);
  -webkit-backdrop-filter: blur(40px) saturate(155%);
}

.mobile-glass-scene__shimmer {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(125deg, transparent 0%, rgba(255, 255, 255, 0.28) 44%, transparent 58%),
    linear-gradient(205deg, transparent 38%, rgba(255, 255, 255, 0.18) 52%, transparent 66%),
    radial-gradient(ellipse 100% 55% at 50% -5%, rgba(255, 255, 255, 0.42) 0%, transparent 72%);
  opacity: 0.8;
}

@media (max-width: 1023px) {
  .mobile-search-stack--active {
    position: sticky;
    top: 0;
    z-index: 45;
  }

  .mobile-search-stack--active.mobile-search-stack--pinned {
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
  }

  .mobile-search-stack__histogram {
    padding-bottom: 0.25rem;
  }
}
</style>
